import type { Conversation, FrontDeskSettings, VickiMode, VickiRights } from '@deskworks/shared-types';
import { VICKI_MODES, VICKI_RIGHTS } from '@deskworks/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ArrowRight, BookOpen, CalendarCheck, ConciergeBell, Info, MessageSquarePlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ChatScreen } from '@/components/chat/chat-screen';
import { ConversationThread, useConversationLive } from '@/components/chat/conversation-thread';
import { useDeskMentions, VickiTalks } from '@/components/chat/desk-talk';
import { RelativeTime } from '@/components/relative-time';
import { LiveVickiButton, LiveVickiPanel, useLiveVicki, useLiveVickiOn } from '@/components/front-desk/live-vicki';
import { ChatMissions } from '@/components/front-desk/vicki-drive';
import { VickiStatusChip } from '@/components/front-desk/vicki-status';
import { VickiGoals, VickiRhythm } from '@/components/front-desk/vicki-goals';
import { VickiTasks } from '@/components/front-desk/vicki-tasks';
import { useAttachments } from '@/components/resources/resources';
import { DraftPanel } from '@/components/setup/draft-panel';
import { ErrorState, LoadingPage } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage } from '@/lib/api';
import { findAccount, useApiMutation, useIsPhone, useMe, useWorkspace } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { useRealtimeSubscription } from '@/lib/realtime';
import { useToast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { readLastWorkspace } from '@/routes/index-redirect';
import { defaultRuntime, runtimeHint } from '@/routes/start';

type Choice = { label: string; description: string };

/** What VICKI may do in the account, in words (docs/features/front-desk.md#rights-and-mode). */
export const RIGHTS_TEXT: Record<VickiRights, Choice> = {
  read: { label: t('Read'), description: t('VICKI reads everything and proposes. Every change waits for your approval.') },
  read_write: { label: t('Read/Write'), description: t('VICKI changes the account and your connected systems without asking, and tells you what she did.') },
};

/** Who starts, in words. */
export const MODE_TEXT: Record<VickiMode, Choice> = {
  guided: { label: t('Guided'), description: t('VICKI does what you tell her in a chat. On her own she only looks and proposes.') },
  god: { label: t('God mode'), description: t('VICKI starts on her own: she gets to know the business and does what brings your goals closer.') },
};

const EXAMPLES = [
  'Kümmer dich um die offenen SELLRZ-Cases.',
  'What is stuck across the business right now, and why?',
  'I want to sell our product to agencies. Set up what we need.',
];

/** A chat's line in the list: VICKI's round of a day, or who started it. */
export function chatLabel(c: Conversation) {
  if (c.roundDay) return t('Round of {day}', { day: new Date(`${c.roundDay}T12:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) });
  return c.title;
}

/**
 * The Front Desk (docs/features/front-desk.md): the one place of the account where people work with VICKI, in as
 * many chats as they like, all visible to everyone of the account. Desks, Work Items and the Inbox stay under the hood.
 */
export function FrontDeskPage() {
  const { account: param } = useParams({ from: '/_authed/$account/front-desk' });
  const { chat, message } = useSearch({ from: '/_authed/$account/front-desk' });
  const me = useMe();
  const account = me.data ? findAccount(me.data.accounts, param) : undefined;
  const workspaces = useQuery({ queryKey: qk.accountWorkspaces(account?.id ?? ''), queryFn: () => api.accounts.workspaces(account!.id), enabled: !!account });
  const chatRow = useQuery({ queryKey: qk.conversation(chat ?? ''), queryFn: () => api.conversations.get(chat!), enabled: !!chat });
  const last = readLastWorkspace();
  // A chat lives in the account's own workspace (its model and files, docs/features/front-desk.md#tasks).
  const home = workspaces.data?.find((w) => w.systemRole === 'account') ?? workspaces.data?.find((w) => w.id === last) ?? workspaces.data?.[0];
  const workspaceId = chat ? (chatRow.data?.workspaceId ?? '') : (home?.id ?? '');
  useRealtimeSubscription(workspaceId);
  if (chat && chatRow.isError) return <div className="mx-auto max-w-3xl px-4 py-8"><ErrorState error={chatRow.error} onRetry={() => void chatRow.refetch()} /></div>;
  if (!account || !workspaceId) return <LoadingPage />;
  return chat ? (
    <VickiChat account={param} accountId={account.id} workspaceId={workspaceId} conversationId={chat} />
  ) : (
    <FrontDeskStart account={param} accountId={account.id} canEdit={account.role === 'owner' || account.role === 'admin'} workspaceId={workspaceId} initialMessage={message} />
  );
}

/** VICKI's rights and mode; only people change them. */
function ModePicker({ accountId, className }: { accountId: string; className?: string }) {
  const settings = useQuery({ queryKey: qk.frontDeskSettings(accountId), queryFn: () => api.frontDesk.settings(accountId) });
  const save = useApiMutation((patch: Partial<FrontDeskSettings>) => api.frontDesk.setSettings(accountId, patch), {
    invalidate: [qk.frontDeskSettings(accountId)],
    success: (s) => t('VICKI: {rights}, {mode}.', { rights: RIGHTS_TEXT[s.rights].label, mode: MODE_TEXT[s.mode].label }),
  });
  const disabled = !settings.data || save.isPending;
  return (
    <>
      <ChoicePicker
        value={settings.data?.rights}
        values={VICKI_RIGHTS}
        text={RIGHTS_TEXT}
        label={t('VICKI’s rights')}
        disabled={disabled}
        className={className}
        onChange={(rights) => save.mutate({ rights })}
      />
      <ChoicePicker
        value={settings.data?.mode}
        values={VICKI_MODES}
        text={MODE_TEXT}
        label={t('VICKI’s mode')}
        disabled={disabled}
        className={className}
        onChange={(mode) => save.mutate({ mode })}
      />
    </>
  );
}

function ChoicePicker<T extends string>(props: {
  value: T | undefined;
  values: readonly T[];
  text: Record<T, Choice>;
  label: string;
  disabled: boolean;
  className?: string;
  onChange: (value: T) => void;
}) {
  const { value, values, text, label } = props;
  return (
    <Select value={value} onValueChange={(v) => props.onChange(v as T)} disabled={props.disabled}>
      <SelectTrigger size="sm" className={cn('w-32 font-normal', props.className)} aria-label={label} title={value ? text[value].description : undefined}>
        {/* The field shows the choice's name; its description is in the list. */}
        <SelectValue placeholder={label}>{value ? text[value].label : null}</SelectValue>
      </SelectTrigger>
      <SelectContent align="end" className="max-w-[min(20rem,calc(100vw-2rem))]">
        {values.map((v) => (
          <SelectItem key={v} value={v}>
            <div className="flex flex-col py-0.5">
              <span className="font-medium">{text[v].label}</span>
              <span className="text-xs whitespace-normal text-muted-foreground">{text[v].description}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function FrontDeskHeader({ account, accountId }: { account: string; accountId: string }) {
  return (
    <div className="mb-2 flex flex-wrap items-center gap-2 text-[13px] font-medium text-primary">
      <ConciergeBell className="size-4" /> <span className="flex-1">{t('Front Desk')}</span>
      <Button asChild size="sm" variant="ghost" className="font-normal">
        <Link to="/$account/front-desk/knowledge" params={{ account }}>
          <BookOpen /> {t('What VICKI knows')}
        </Link>
      </Button>
      <ModePicker accountId={accountId} />
    </div>
  );
}

function FrontDeskStart({
  account,
  accountId,
  canEdit,
  workspaceId,
  initialMessage,
}: {
  account: string;
  accountId: string;
  /** Owners and admins set VICKI's goals. */
  canEdit: boolean;
  workspaceId: string;
  initialMessage?: string;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  // VICKI thinks with any runtime of the account (docs/features/front-desk.md#tasks).
  const profiles = useQuery({ queryKey: qk.frontDeskRuntimes(accountId), queryFn: () => api.frontDesk.runtimes(accountId) });
  const chats = useQuery({ queryKey: qk.frontDeskConversations(accountId), queryFn: () => api.frontDesk.conversations(accountId) });
  const [message, setMessage] = useState(initialMessage ?? '');
  const [runtimeId, setRuntimeId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [allChats, setAllChats] = useState(false);
  const phone = useIsPhone();
  useEffect(() => setMessage(initialMessage ?? ''), [initialMessage]);
  const usable = (profiles.data ?? []).filter((p) => p.status === 'active' && p.implemented);
  const runtime = usable.find((p) => p.id === runtimeId) ?? defaultRuntime(profiles.data ?? []);

  const start = async () => {
    if (!message.trim() || !runtime) return;
    setBusy(true);
    try {
      const conv = await api.frontDesk.start(workspaceId, { message: message.trim(), runtimeProfileId: runtime.id });
      void queryClient.invalidateQueries({ queryKey: qk.frontDeskConversations(accountId) });
      void navigate({ to: '/$account/front-desk', params: { account }, search: { chat: conv.id } });
    } catch (err) {
      toast.error(t('Could not start'), errorMessage(err));
      setBusy(false);
    }
  };

  if (profiles.isPending) return <LoadingPage />;
  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-5 sm:px-8 sm:py-16">
      <FrontDeskHeader account={account} accountId={accountId} />
      <h1 className="text-xl font-semibold tracking-tight sm:text-3xl">{t('What can VICKI do for you?')}</h1>
      <p className="mt-2 hidden text-sm text-muted-foreground sm:block">
        {t(
          'VICKI runs the business with you: she understands what you want, asks what she needs, sets up the desks that do the work, hands work to them and brings back the results. Everyone in the account sees these chats.',
        )}
      </p>
      {!usable.length ? (
        <Card className="mt-4 p-5 text-sm sm:mt-8">
          <div className="font-medium">{t('Connect a model first')}</div>
          <div className="text-muted-foreground">{t('VICKI needs a model to think with: your Claude or ChatGPT subscription, or an API key.')}</div>
        </Card>
      ) : (
        <Card className="mt-4 p-3 sm:mt-8">
          <Textarea
            autoFocus={!phone}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void start();
            }}
            rows={phone ? 3 : 4}
            placeholder={t('e.g. Take care of the open SELLRZ cases.')}
            className="resize-none border-0 text-base shadow-none focus-visible:ring-0 sm:text-[15px]"
          />
          <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
            <div className="flex min-w-0 flex-1 items-center gap-2 text-xs text-muted-foreground">
              {t('Model')}
              <Select value={runtime?.id} onValueChange={setRuntimeId}>
                <SelectTrigger size="sm" className="w-full max-w-56 min-w-0">
                  <SelectValue placeholder={t('Choose a runtime')} />
                </SelectTrigger>
                <SelectContent>
                  {usable.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                      {runtimeHint(p)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => void start()} disabled={!message.trim()} loading={busy}>
              {t('Start')} <ArrowRight />
            </Button>
          </div>
        </Card>
      )}
      {usable.length > 0 && !message && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1 sm:mt-4 sm:flex-wrap sm:overflow-visible">
          {EXAMPLES.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => setMessage(e)}
              className="shrink-0 rounded-full border px-3 py-1 text-xs whitespace-nowrap text-muted-foreground hover:bg-muted hover:text-foreground sm:whitespace-normal"
            >
              {e}
            </button>
          ))}
        </div>
      )}
      {/* The chats come first: on a phone the Front Desk is a chat app (docs/features/mobile.md). */}
      {(chats.data?.length ?? 0) > 0 && (
        <div className="mt-8 sm:mt-12">
          <h2 className="mb-2 text-sm font-medium text-muted-foreground">{t('Chats with VICKI')}</h2>
          <Card className="divide-y overflow-hidden">
            {chats.data!.slice(0, allChats ? 200 : 6).map((c) => (
              <Link
                key={c.id}
                to="/$account/front-desk"
                params={{ account }}
                search={{ chat: c.id }}
                className="flex items-start gap-3 px-4 py-3 text-sm hover:bg-muted/50 active:bg-muted/70"
              >
                <span
                  className={cn(
                    'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full',
                    c.roundDay ? 'bg-primary/10 text-primary' : 'bg-gradient-to-br from-primary to-violet-500 text-[11px] font-bold text-white',
                  )}
                  aria-hidden
                >
                  {c.roundDay ? <CalendarCheck className="size-4" /> : 'V'}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline gap-2">
                    <span className="min-w-0 flex-1 truncate font-medium">{chatLabel(c)}</span>
                    <RelativeTime className="shrink-0 text-xs text-muted-foreground" value={c.lastMessageAt ?? c.createdAt} />
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {c.startedBy ? `${c.startedBy.name} · ` : ''}
                    {c.lastMessagePreview ?? ''}
                  </span>
                </span>
              </Link>
            ))}
          </Card>
          {!allChats && chats.data!.length > 6 && (
            <Button variant="ghost" size="sm" className="mt-1" onClick={() => setAllChats(true)}>
              {t('Show all ({n})', { n: chats.data!.length })}
            </Button>
          )}
        </div>
      )}
      {/* What VICKI asked the desks, chat by chat (docs/features/desk-talk.md). */}
      <VickiTalks accountId={accountId} workspaceId={workspaceId} />
      {/* What VICKI works on in the background, what she works towards, and how often she looks (docs/features/front-desk.md). */}
      <div className="mt-8 flex flex-col gap-3 sm:mt-10">
        <VickiTasks accountId={accountId} />
        <VickiGoals accountId={accountId} canEdit={canEdit} />
        <VickiRhythm accountId={accountId} />
      </div>
    </div>
  );
}

function VickiChat({ account, accountId, workspaceId, conversationId }: { account: string; accountId: string; workspaceId: string; conversationId: string }) {
  const navigate = useNavigate();
  const phone = useIsPhone();
  const { query: conv, live } = useConversationLive(conversationId);
  const attachments = useAttachments(workspaceId, conversationId);
  // @Desk asks a desk right away (docs/features/desk-talk.md); its answer shows here.
  const mentions = useDeskMentions(workspaceId);
  const [tab, setTab] = useState<'chat' | 'draft'>('chat');
  // Talking with her (docs/features/front-desk.md#live-vicki): what she writes shows in the chat below.
  const liveOn = useLiveVickiOn(accountId);
  const voice = useLiveVicki(conversationId);
  // A setup VICKI drafts shows next to the chat (docs/features/front-desk.md#setup); without one the chat takes the width.
  const draftCount = useQuery({ queryKey: qk.setupDraft(conversationId), queryFn: () => api.setup.draft(conversationId) }).data?.draft?.changes.length ?? 0;
  useEffect(() => setTab('chat'), [conversationId]);
  if (conv.isPending) return <LoadingPage />;
  if (conv.isError) return <div className="mx-auto max-w-3xl px-4 py-8"><ErrorState error={conv.error} onRetry={() => void conv.refetch()} /></div>;
  const c = conv.data;
  const thread = (className: string) => (
    <ConversationThread
      className={className}
      conv={c}
      agentName={c.agentName}
      workspaceId={workspaceId}
      live={live}
      autoFocus={!phone}
      attachments={attachments}
      mentions={mentions}
      // Talking with her sits in the field's row, next to writing (docs/features/front-desk.md#live-vicki).
      composerExtra={(liveOn || voice.active) && <LiveVickiButton live={voice} compact className="size-9 rounded-full" />}
      placeholder={t('Tell VICKI what to do … (@ asks a desk)')}
      // What she keeps at here, above the field (docs/features/front-desk.md#driving-a-chat).
      banner={<ChatMissions conversationId={conversationId} />}
      intro={
        <div className="flex items-start gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          <Info className="mt-0.5 size-3.5 shrink-0" />
          {t('Everyone in the account sees this chat. What VICKI may do depends on her rights and mode; what waits for you shows up here as a card.')}
        </div>
      }
    />
  );
  if (phone) {
    // On a phone the chat is the whole screen (docs/features/mobile.md#chats): back to the Front Desk, her mode and the
    // draft under the title, the field to write in above the keyboard.
    return (
      <ChatScreen
        title={chatLabel(c)}
        subtitle={<>VICKI{c.startedBy ? ` · ${c.startedBy.name}` : ''}</>}
        onBack={() => void navigate({ to: '/$account/front-desk', params: { account }, search: {} })}
        actions={
          <>
            <Button asChild variant="ghost" size="icon" className="size-10" aria-label={t('New chat')}>
              <Link to="/$account/front-desk" params={{ account }} search={{}}>
                <MessageSquarePlus />
              </Link>
            </Button>
          </>
        }
        toolbar={
          <>
            <VickiStatusChip conv={c} className="h-8" />
            <ModePicker accountId={accountId} className="h-8 w-auto" />
            {draftCount > 0 && (
              <Button size="sm" variant={tab === 'draft' ? 'secondary' : 'outline'} className="h-8 shrink-0" onClick={() => setTab(tab === 'draft' ? 'chat' : 'draft')}>
                {tab === 'draft' ? t('Chat') : t('Draft ({n})', { n: draftCount })}
              </Button>
            )}
            <Button asChild size="sm" variant="ghost" className="h-8 shrink-0 font-normal">
              <Link to="/$account/front-desk/knowledge" params={{ account }}>
                <BookOpen /> {t('What VICKI knows')}
              </Link>
            </Button>
          </>
        }
      >
        <LiveVickiPanel live={voice} />
        {tab === 'draft' && draftCount > 0 ? <DraftPanel conversationId={conversationId} workspaceId={workspaceId} className="min-h-0 flex-1" /> : thread('min-h-0 flex-1')}
      </ChatScreen>
    );
  }
  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b bg-background px-4 py-3 sm:px-6">
        <ConciergeBell className="size-4 text-primary" />
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-base font-semibold">{chatLabel(c)}</h1>
          <div className="text-xs text-muted-foreground">
            {t('Front Desk')} · VICKI
            {c.startedBy ? ` · ${t('started by {name}', { name: c.startedBy.name })}` : ''}
            {c.runtime ? ` · ${c.runtime}` : ''}
          </div>
        </div>
        {draftCount > 0 && (
          <div className="inline-flex rounded-md border p-0.5 lg:hidden">
            <Button size="xs" variant={tab === 'chat' ? 'secondary' : 'ghost'} onClick={() => setTab('chat')}>
              {t('Chat')}
            </Button>
            <Button size="xs" variant={tab === 'draft' ? 'secondary' : 'ghost'} onClick={() => setTab('draft')}>
              {t('Draft ({n})', { n: draftCount })}
            </Button>
          </div>
        )}
        <VickiStatusChip conv={c} />
        <ModePicker accountId={accountId} />
        <Button asChild size="sm" variant="outline">
          <Link to="/$account/front-desk" params={{ account }} search={{}}>
            <MessageSquarePlus /> {t('New')}
          </Link>
        </Button>
      </header>
      <LiveVickiPanel live={voice} />
      <div className={draftCount > 0 ? 'grid min-h-0 flex-1 lg:grid-cols-[minmax(0,1fr)_440px]' : 'flex min-h-0 flex-1 flex-col'}>
        {thread(tab === 'chat' || !draftCount ? 'min-h-0 flex-1' : 'hidden min-h-0 lg:flex')}
        {draftCount > 0 && <DraftPanel conversationId={conversationId} workspaceId={workspaceId} className={tab === 'draft' ? 'min-h-0' : 'hidden min-h-0 lg:flex'} />}
      </div>
    </div>
  );
}

/** A chat with VICKI reached through a workspace's address (an old link, a notification): it opens at the Front Desk. */
export function ToFrontDeskChat({ workspaceId, conversationId }: { workspaceId: string; conversationId: string }) {
  const navigate = useNavigate();
  const me = useMe();
  const workspace = useWorkspace(workspaceId);
  const account = me.data && workspace.data ? me.data.accounts.find((a) => a.id === workspace.data!.accountId) : undefined;
  useEffect(() => {
    if (account) void navigate({ to: '/$account/front-desk', params: { account: account.slug || account.id }, search: { chat: conversationId }, replace: true });
  }, [account, conversationId, navigate]);
  return <LoadingPage />;
}
